// // src/components/Center Admin/forms/SalivaSampleForm.tsx

import React, { useEffect, useState } from "react";
import axios from "axios";
import "../../../center admin css/SalivaSampleForm.css";
import "../../../center admin css/centerAdmin.css";
import api from "../../../api/axiosConfig";

interface FormComponentProps {
  emrId: string;
  onComplete?: () => void;
}

export const SalivaSampleForm: React.FC<FormComponentProps> = ({
  emrId,
  onComplete,
}) => {
  console.log("The EMR ID for this form is:", emrId);

  const [checks, setChecks] = useState({
    lactobacilliCollected: false,
    streptMutansCollected: false,
    salivaSecretionCollected: false,
    salivaBufferCollected: false,
  });

  const [loading, setLoading] = useState(false);

  const labels: Record<keyof typeof checks, string> = {
    lactobacilliCollected: "Lactobacilli in Saliva Sample Collected?",
    streptMutansCollected: "Streptococci Mutans Sample Collected?",
    salivaSecretionCollected: "Saliva Secretion Sample Collected?",
    salivaBufferCollected: "Saliva Buffer Capacity (pH) Sample Collected?",
  };

  const handleChange =
    (key: keyof typeof checks) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setChecks((prev) => ({ ...prev, [key]: e.target.checked }));
    };

  const resetForm = () => {
    setChecks({
      lactobacilliCollected: false,
      streptMutansCollected: false,
      salivaSecretionCollected: false,
      salivaBufferCollected: false,
    });
  };

  /* ---------------------------------------------------------
   LOAD EXISTING SALIVA SAMPLE STATUS
---------------------------------------------------------- */
  const loadExistingSalivaSample = async () => {
    if (!emrId) return;

    try {
      const res = await api.dataCollectionApi.get(
        `/api/forms/cariogram-saliva/${encodeURIComponent(emrId)}`,
      );

      const saved = res.data?.data?.["Cariogram - Saliva Sample Status"];

      if (!saved) {
        resetForm();
        return;
      }

      setChecks({
        lactobacilliCollected:
          !!saved["Lactobacilli in Saliva Sample Collected?"],
        streptMutansCollected: !!saved["Streptococci Mutans Sample Collected?"],
        salivaSecretionCollected: !!saved["Saliva Secretion Sample Collected?"],
        salivaBufferCollected:
          !!saved["Saliva Buffer Capacity (pH) Sample Collected?"],
      });
    } catch (err: any) {
      // No existing record is okay.
      if (err?.response?.status === 404) {
        resetForm();
        return;
      }

      console.error("Failed to load existing Saliva Sample Status:", err);
      resetForm();
    }
  };

  useEffect(() => {
    loadExistingSalivaSample();
  }, [emrId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      emrId,
      "Cariogram - Saliva Sample Status": {
        "Lactobacilli in Saliva Sample Collected?":
          checks.lactobacilliCollected,
        "Streptococci Mutans Sample Collected?": checks.streptMutansCollected,
        "Saliva Secretion Sample Collected?": checks.salivaSecretionCollected,
        "Saliva Buffer Capacity (pH) Sample Collected?":
          checks.salivaBufferCollected,
      },
    };

    try {
      setLoading(true);
      console.log("Sending payload:", payload);

      const res = await api.dataCollectionApi.post(
        "/api/forms/cariogram-saliva",
        payload,
        { headers: { "Content-Type": "application/json" } },
      );

      if (res.data?.success) {
        alert("Saliva sample status saved successfully!");

        resetForm();

        // ✅ only after successful validation
        onComplete && onComplete();
      } else {
        throw new Error(res.data?.message || "Save failed");
      }
    } catch (err: any) {
      alert(`Error: ${err?.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="saliva-form-container" onSubmit={handleSubmit}>
      {/* HEADER */}
      <div className="vs-section-bar">Cariogram - Saliva Sample Status</div>

      {/* CHECKBOX GRID */}
      <div className="saliva-grid">
        {Object.entries(checks).map(([key, value]) => (
          <label key={key} className="saliva-check-item">
            <input
              type="checkbox"
              checked={value}
              onChange={handleChange(key as keyof typeof checks)}
            />
            <span className="saliva-custom-circle"></span>
            <span className="saliva-label">
              {labels[key as keyof typeof checks]}
            </span>
          </label>
        ))}
      </div>

      {/* ACTION BUTTONS */}
      <div className="saliva-actions">
        <button type="button" className="saliva-btn saliva-btn-secondary">
          Close
        </button>
        <button
          type="submit"
          className="saliva-btn saliva-btn-primary"
          disabled={loading}
        >
          {loading ? "Submitting..." : "Sample Collected"}
        </button>
      </div>
    </form>
  );
};

export default SalivaSampleForm;
